import {useMessage} from '@coolcolduk/ui-mui';
import {Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography} from '@mui/material';
import {useState} from 'react';
import {EtsyListingStateEnum} from '@coolcolduk/etsy';
import EtsyListingStateSelector from '../../../components/etsy/EtsyListingStateSelector';
import useGdriveTokens from '../../../hook/useGdriveTokens';
import {HookAccountToken} from '../../../hook/useGetAllAccountToken';
import useCreateEtsyBulkUpdateGoogleSheetLink from './useCreateEtsyBulkUpdateGoogleSheetLink';

export interface EtsyBulkUpdateGoogleSheetAddLinkFormProps {
  selectedAccount: HookAccountToken | undefined;
}

export default function EtsyBulkUpdateGoogleSheetAddLinkForm({
  selectedAccount,
}: EtsyBulkUpdateGoogleSheetAddLinkFormProps) {
  const {showMessage} = useMessage();
  const [linkName, setLinkName] = useState('');
  const [spreadsheetUrlOrId, setSpreadsheetUrlOrId] = useState('');
  const [listingState, setListingState] = useState<EtsyListingStateEnum>(EtsyListingStateEnum.ACTIVE);
  const [gdriveTokenId, setGdriveTokenId] = useState('');

  const {completedTokens: gdriveTokens, loading: gdriveLoading} = useGdriveTokens();
  const [createLink, {loading: createLoading}] = useCreateEtsyBulkUpdateGoogleSheetLink({
    onCompleted: () => {
      showMessage('Link added');
      setLinkName('');
      setSpreadsheetUrlOrId('');
      setGdriveTokenId('');
    },
    onError: (e) => showMessage(e.message, undefined, 'error'),
  });

  const handleAddLink = () => {
    if (!selectedAccount) return;
    if (!gdriveTokenId.trim()) {
      showMessage('Select a Google Drive account', undefined, 'warning');
      return;
    }
    if (!spreadsheetUrlOrId.trim()) {
      showMessage('Enter spreadsheet URL or id', undefined, 'warning');
      return;
    }
    createLink({
      name: linkName.trim() || undefined,
      etsyAccountId: selectedAccount.id,
      spreadsheetUrlOrId: spreadsheetUrlOrId.trim(),
      listingState,
      gdriveTokenId: gdriveTokenId.trim(),
    });
  };

  return (
    <Stack spacing={2} sx={{maxWidth: 480}}>
      <TextField
        fullWidth
        label="Name (optional)"
        value={linkName}
        onChange={(e) => setLinkName(e.target.value)}
        placeholder="e.g. Main shop – active listings"
      />
      <Typography variant="body2" color="text.secondary">
        Store: {selectedAccount?.name ?? '—'} (change account in header)
      </Typography>
      <TextField
        fullWidth
        label="Google Sheet URL or id"
        value={spreadsheetUrlOrId}
        onChange={(e) => setSpreadsheetUrlOrId(e.target.value)}
        placeholder="https://docs.google.com/spreadsheets/d/..."
        required
      />
      <EtsyListingStateSelector listingState={listingState} setListingState={(e) => e && setListingState(e)} />
      <FormControl fullWidth required>
        <InputLabel id="gdrive-link-label">Google Drive account</InputLabel>
        <Select
          labelId="gdrive-link-label"
          value={gdriveTokenId}
          label="Google Drive account"
          onChange={(e) => setGdriveTokenId(e.target.value)}
          disabled={gdriveLoading || gdriveTokens.length === 0}
        >
          {gdriveTokens.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name} {t.email ? `(${t.email})` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        onClick={handleAddLink}
        disabled={
          createLoading ||
          !selectedAccount ||
          !spreadsheetUrlOrId.trim() ||
          !gdriveTokenId.trim() ||
          gdriveTokens.length === 0
        }
      >
        Add link
      </Button>
    </Stack>
  );
}
